import { calculateIncrements, DEFAULT_SCORE_PRECISION_DIGITS, MAX_SCORE_PRECISION_DIGITS } from "@minglun/domain";
export function clampObjectiveWeight(value) {
    return Math.round(Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0)));
}
export function weightFocusExponent(request) {
    const value = request.workbench.globalSettings.weightFocusExponent;
    return Math.max(1, Math.min(10, Number.isFinite(value) ? value : 2));
}
export function scorePrecisionDigits(request) {
    const value = request.workbench.globalSettings.scorePrecisionDigits;
    return Math.max(0, Math.min(MAX_SCORE_PRECISION_DIGITS, Math.floor(Number.isFinite(value) ? value : DEFAULT_SCORE_PRECISION_DIGITS)));
}
export function scorePrecisionUnit(request) {
    return 10 ** -scorePrecisionDigits(request);
}
export function focusedWeight(weight, exponent) {
    return Math.pow(clampObjectiveWeight(weight), exponent);
}
export function enabledObjectives(request) {
    return request.userPlan.objectives.filter((objective) => objective.isEnabled && clampObjectiveWeight(objective.weight) > 0);
}
export function calculateGainsUtility(request, gains) {
    const increments = calculateIncrements(request.workbench, request.userPlan.baseAttributes, gains);
    return calculateIncrementsUtility(request, increments);
}
export function calculateIncrementsUtility(request, increments) {
    const baselines = new Map(request.workbench.globalSettings.objectiveBaselines.map((item) => [
        item.metricId,
        item.value,
    ]));
    const exponent = weightFocusExponent(request);
    return enabledObjectives(request).reduce((total, objective) => {
        const baseline = baselines.get(objective.metricId) ?? 0;
        if (!(baseline > 0))
            return total;
        return (total +
            ((increments[objective.metricId] ?? 0) / baseline) *
                focusedWeight(objective.weight, exponent));
    }, 0);
}
export function calculateIdealUtility(request) {
    const { workbench, userPlan } = request;
    const categories = new Map(workbench.wheelCategories.map((category) => [category.id, category]));
    const characters = new Map(workbench.characters.map((character) => [character.id, character]));
    const maxRank = Math.max(1, workbench.globalSettings.maxRank);
    let ideal = 0;
    for (const wheel of workbench.fateWheels) {
        const category = categories.get(wheel.wheelCategoryId);
        if (!wheel.isEnabled ||
            !category?.isEnabled ||
            (category.isDestiny && !userPlan.solver.includeDestinyWheel) ||
            !wheel.members.length)
            continue;
        const memberIds = new Set();
        if (wheel.members.some((member) => memberIds.has(member.characterId) ||
            (memberIds.add(member.characterId), false) ||
            !characters.get(member.characterId)?.isEnabled))
            continue;
        const starUtility = calculateGainsUtility(request, wheel.perStarGains);
        const rankUtility = calculateGainsUtility(request, wheel.perRankGains);
        // 评分的 100 分基准是所有当前可用关系命轮全部升至满阶，
        // 与库存、碎片约束和求解器最终是否选择它们无关。
        ideal += maxRank * (wheel.maxStar * starUtility + rankUtility);
    }
    return ideal;
}
export function calculateScoreFromUtility(request, utility) {
    const ideal = calculateIdealUtility(request);
    if (!(ideal > 0))
        return 0;
    return normalizeScore(Math.max(0, Math.min(100, (utility / ideal) * 100)));
}
/**
 * 将各线性收益项直接换算为 0–100 分制的评分系数。全部系数
 * 使用同一理论满激活基准，因此不再出现原始属性大数吞小数。
 */
export function scoreCoefficients(request, values) {
    const ideal = calculateIdealUtility(request);
    if (!(ideal > 0))
        return values.map(() => 0);
    return values.map((value) => value / ideal * 100);
}
export function normalizeScore(value) {
    return Number(value.toFixed(8));
}
//# sourceMappingURL=scoring.js.map